import { forwardRef } from "react";
import type { HTMLAttributes } from "react";
import "./Pagination.css";

export interface PaginationProps extends HTMLAttributes<HTMLElement> {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** How many neighbour pages to show on each side of the current page. */
  siblingCount?: number;
  /** How many pages to always show at the start/end. */
  boundaryCount?: number;
  prevLabel?: string;
  nextLabel?: string;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const Arrow = ({ dir }: { dir: "left" | "right" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {dir === "left" ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 6l6 6-6 6" />}
  </svg>
);

function buildRange(
  page: number,
  pageCount: number,
  siblingCount: number,
  boundaryCount: number,
): Array<number | "ellipsis"> {
  const range: Array<number | "ellipsis"> = [];
  const start = Math.max(2, page - siblingCount);
  const end = Math.min(pageCount - 1, page + siblingCount);

  for (let i = 1; i <= Math.min(boundaryCount, pageCount); i++) range.push(i);
  if (start > boundaryCount + 1) range.push("ellipsis");
  for (let i = Math.max(boundaryCount + 1, start); i <= end; i++) range.push(i);
  if (end < pageCount - boundaryCount) range.push("ellipsis");
  for (let i = Math.max(pageCount - boundaryCount + 1, end + 1); i <= pageCount; i++) range.push(i);
  return Array.from(new Set(range));
}

export const Pagination = forwardRef<HTMLElement, PaginationProps>(function Pagination(
  {
    page,
    pageCount,
    onPageChange,
    siblingCount = 1,
    boundaryCount = 1,
    prevLabel = "Previous page",
    nextLabel = "Next page",
    className,
    ...rest
  },
  ref,
) {
  const range = buildRange(page, pageCount, siblingCount, boundaryCount);
  return (
    <nav ref={ref} aria-label="Pagination" className={cx("go4-pagination", className)} {...rest}>
      <button
        type="button"
        className="go4-pagination__btn"
        aria-label={prevLabel}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <Arrow dir="left" />
      </button>
      {range.map((entry, idx) =>
        entry === "ellipsis" ? (
          <span key={`e-${idx}`} className="go4-pagination__ellipsis" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            aria-current={entry === page ? "page" : undefined}
            className={cx("go4-pagination__btn", entry === page && "go4-pagination__btn--active")}
            onClick={() => onPageChange(entry)}
          >
            {entry}
          </button>
        ),
      )}
      <button
        type="button"
        className="go4-pagination__btn"
        aria-label={nextLabel}
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
      >
        <Arrow dir="right" />
      </button>
    </nav>
  );
});
